import {
  AddressOptions,
  SelectAddressShape,
} from '@rabbit/elements/shared-types';
import { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  onPostCodeChange,
  onRetreiveFullAddress,
  onGetIoSuggestions,
  onRetrieveIoFullAddress,
} from '../../../utils/helpers';
import { generateSelectStyles } from '../../../utils/consts';
import { nestApiGetSecret } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';


export interface InputTypeSelectAddressProps {
  onChange: (address: SelectAddressShape) => void;
  label?: string;
  errors: any;
  country?: string;
}

let fetchifyKeyCache: string | null = null;
let getAddressIoKeyCache: string | null = null;
async function getSecretKeyGeneric(
  secretName: string,
  envVarName: string,
  getCache: () => string | null,
  setCache: (v: string) => void
): Promise<string> {
  // Return cached key if available
  const cached = getCache();
  if (cached) return cached;

  try {
    const result = await nestApiGetSecret({ secretName });
    if (result.data.success && result.data.value) {
      setCache(result.data.value);
      const temp = result.data.value;
      return result.data.value;
    } else {
      throw new Error(result.data.error || `Failed to retrieve ${secretName}`);
    }
  } catch (error) {
    console.error(`Error fetching ${secretName}:`, error);
    // Fallback to environment variable if API fails (dynamic access)
    const fallbackKey = (import.meta as any).env?.[envVarName];
    if (fallbackKey) {
      console.warn(
        `Using fallback ${secretName} from environment variable ${envVarName}`
      );
      setCache(fallbackKey);
      return fallbackKey;
    }
    throw new Error(`${secretName} not available from API or environment`);
  }
}

export function InputTypeSelectAddress(props: InputTypeSelectAddressProps) {
  const { onChange, label, errors, country } = props;
  const selectInputRef = useRef<any>(null);
  const { config } = useContext(AppContext);

  const USE_GOADDRESS_LOOKUP =
    config?.CUSTOMER?.USE_GOADDRESS_LOOKUP &&
    country &&
    ['GB', 'UK'].includes(country);

  const USE_FALLBACK_FETCHIFY = config?.CUSTOMER?.USE_FALLBACK_FETCHIFY;
  const [disableAddress, setDisableAddress] = useState(false);

  const [selectedAddress, setSelectedAddress] =
    useState<SelectAddressShape | null>(null);
  const [isFetchifyKeyReady, setIsFetchifyKeyReady] = useState(false);
  const [isGoAddressKeyReady, setIsGoAddressKeyReady] = useState(false);

  useEffect(() => {
    if (selectInputRef.current) {
      setSelectedAddress(null);
      selectInputRef.current.clearValue();
    }
  }, [country]);

  // Specific wrappers using the generic helper
  const getFetchifyKey = useCallback(async (): Promise<string> => {
    return getSecretKeyGeneric(
      'FETCHIFY_KEY',
      'VITE_FETCHIFY_KEY',
      () => fetchifyKeyCache,
      (v) => {
        fetchifyKeyCache = v;
      }
    );
  }, []);

  const getGetAddressIoKey = useCallback(async (): Promise<string> => {
    return getSecretKeyGeneric(
      'GO_ADDRESS_API_KEY',
      'VITE_GO_ADDRESS_API_KEY',
      () => getAddressIoKeyCache,
      (v: string) => {
        getAddressIoKeyCache = v;
      }
    );
  }, []);

  // Initialize provider readiness
  useEffect(() => {
    const init = async () => {
      try {
        if (USE_GOADDRESS_LOOKUP) {
          setIsGoAddressKeyReady(false);
          await getGetAddressIoKey();
          setIsGoAddressKeyReady(true);
        } else {
          setIsFetchifyKeyReady(false);
          await getFetchifyKey();
          setIsFetchifyKeyReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize address provider key:', error);
      }
    };

    init();
  }, [USE_GOADDRESS_LOOKUP, getFetchifyKey, getGetAddressIoKey]);

  const parseAddresses = (data: Array<any>): AddressOptions[] => {
    const addressesOptions: AddressOptions[] = [];
    data.map((item: { count: number; id: string; labels: string[] }) => {
      if (item.count === 1) {
        addressesOptions.push({
          value: item.id,
          label: `${item.labels[0]} ${item.labels[1]}`,
        });
      }
    });
    return addressesOptions;
  };

  const getAddressSuggestions = async (
    value: string
  ): Promise<AddressOptions[]> => {
    try {
      // GoAddress: pass raw input (postcode, address, etc.) directly to search
      if (USE_GOADDRESS_LOOKUP) {
        if (!value.trim()) return [];
        if (!getAddressIoKeyCache) {
          console.warn(
            'GO_ADDRESS_API_KEY key not available, skipping address suggestions'
          );
          return [];
        }
        return await onGetIoSuggestions(value.trim(), getAddressIoKeyCache);
      }

      // Use cached key directly - no need to call getFetchifyKey again
      if (!fetchifyKeyCache) {
        console.warn(
          'FETCHIFY_KEY not available, skipping address suggestions'
        );
        return [];
      }
      if (USE_FALLBACK_FETCHIFY) {
        const data = await onPostCodeChange(value, fetchifyKeyCache, country);
        if (data) return parseAddresses(JSON.parse(data).results);
        return [];
      }
      return [];
    } catch (error) {
      console.error('Error getting address suggestions:', error);
      return [];
    }
  };

  const getAddressDetails = async (value: string) => {
    try {
      if (USE_GOADDRESS_LOOKUP) {
        // value is "ADDRESS:{...}" (full address encoded by onGetIoSuggestions)
        if (value.startsWith('ADDRESS:')) {
          try {
            const parsed = JSON.parse(
              value.slice(8)
            ) as SelectAddressShape | null;
            if (parsed && typeof parsed.line_1 !== 'undefined') {
              setSelectedAddress(parsed);
              return;
            }
          } catch {
            // fall through to API call if parse fails
          }
        }
        // Fallback: query GoAddress directly with the raw value
        const response = await onRetrieveIoFullAddress(
          value.trim(),
          getAddressIoKeyCache || ''
        );
        if (response) setSelectedAddress(response);
        return;
      }

      // Fetchify flow
      if (!fetchifyKeyCache) {
        console.warn('FETCHIFY_KEY not available, skipping address details');
        return;
      }

      const data = await onRetreiveFullAddress(
        value,
        fetchifyKeyCache,
        country
      );
      if (data) setSelectedAddress(JSON.parse(data).result);
    } catch (error) {
      console.error('Error getting address details:', error);
    }
  };

  const isProviderReady = USE_GOADDRESS_LOOKUP
    ? isGoAddressKeyReady
    : isFetchifyKeyReady;

  //get address details when address is selected
  const onSelectedAddress = (selected: SingleValue<AddressOptions>) => {
    if (selected?.value) void getAddressDetails(selected.value);
    else setSelectedAddress(null);
  };

  const loadOptions = (
    inputValue: string,
    callback: (options: AddressOptions[]) => void
  ) => {
    if (inputValue === '') return callback([]);
    setTimeout(async () => {
      callback(await getAddressSuggestions(inputValue));
    }, 500);
  };

  useEffect(() => {
    selectInputRef?.current?.clearValue();
  }, [country]);

  useEffect(() => {
    if (selectedAddress) {
      // Always propagate the selection, even if fields are empty; let parent handle clearing/validation.
      onChange(selectedAddress);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (country) {
      if (USE_GOADDRESS_LOOKUP && !['GB', 'UK'].includes(country)) {
        setDisableAddress(true);
      } else if (
        !config.CUSTOMER.USE_FALLBACK_FETCHIFY &&
        country !== 'GB'
      ) {
        setDisableAddress(true);
      } else {
        setDisableAddress(false);
      }
    }
  }, [country, USE_GOADDRESS_LOOKUP, config.CUSTOMER.USE_FALLBACK_FETCHIFY]);

  return (
    <>
      <label
        htmlFor={'address-selector'}
        className={'font-nunito text-base text-gray-900'}
      >
        {label ?? 'Find an address'}
      </label>
      <div className="mb-2"></div>
      <AsyncSelect
        ref={selectInputRef}
        cacheOptions
        loadOptions={loadOptions}
        defaultOptions
        classNames={generateSelectStyles()}
        isMulti={false}
        placeholder={
          isProviderReady
            ? USE_GOADDRESS_LOOKUP
              ? 'Enter postcode (e.g. SW1A 2AA)'
              : 'Start with postcode or street'
            : 'Loading address service...'
        }
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? USE_GOADDRESS_LOOKUP
              ? 'Enter a postcode to search'
              : 'Start typing to search'
            : 'No address found'
        }
        onChange={onSelectedAddress}
        isDisabled={
          !country || country === '' || !isProviderReady || disableAddress
        }
        isLoading={!isProviderReady}
      />
      {/* <Select
        ref={selectInputRef}
        name="address-selector"
        options={options}
        isMulti={false}
        classNames={generateSelectStyles()}
        //styles={customStyles}
        //onChange={onSelectedAddress}
        onInputChange={onInputChange}
        //isClearable={true}
        placeholder="Start with postcode or street"
        // noOptionsMessage={({ inputValue }) =>
        //   !inputValue
        //     ? 'Start with postcode or street'
        //     : query === debouncedQuery && !isLoadingResults
        //     ? 'No results found'
        //     : 'Loading...'
        // }
        isDisabled={!country || country === '' ? true : false}
      /> */}
    </>
  );
}

export default InputTypeSelectAddress;
